import { pick } from 'lodash';
import dayjs from 'dayjs';

import cookie from '../../shared/utils/cookie';
import { getCurrentDomain } from '../../shared/utils/host';

import {
  bodyScroll,
  addCloseButton,
  addLanguageClassForContainer,
  setPreferencesCategoryValuesByLanguage
} from './helpers';

const DEFAULT_LANGUAGE = 'en';
const DEFAULT_COUNTRY = 'UK';

const isBrowser = !!window;

export default class CookieConsentHandler {
  constructor(config, lib) {
    this._config = config;
    this._lib = lib;
    this._categories = this._getCategories();
    this._consent = null;
    this._cookieOptions = {
      domain: this._config.cookie.domain,
      path: this._config.cookie.path
    };
    this.geo = { country: DEFAULT_COUNTRY };
    this._language = DEFAULT_LANGUAGE;

    this.onFirstConsent = this.onFirstConsent.bind(this);
    this.onConsent = this.onConsent.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onModalShow = this.onModalShow.bind(this);
    this.onModalHide = this.onModalHide.bind(this);
  }

  async onFirstConsent({ cookie }) {
    try {
      this._saveConsentToMemory(cookie);
      this._clearConsent();
      await this._sendConsentToLogger();
      this._saveConsent();
      this._applyConsent();
      this._sendUpdateEventToGA();
    } catch (err) {
      console.error(err.message);
      this._clearConsent();
      window.location.reload();
    }
  }

  onConsent({ cookie }) {
    try {
      this._saveConsentToMemory(cookie);
      this._applyConsent();
    } catch (err) {
      console.error(err.message);
    }
  }

  async onChange({ cookie, changedCategories }) {
    try {
      this._saveConsentToMemory(cookie);
      this._clearConsent();
      await this._sendConsentToLogger();
      this._saveConsent();
      this._applyConsent();
      this._sendUpdateEventToGA();
      this._checkChangedConsent(changedCategories);
    } catch (err) {
      console.error(err.message);
      this._clearConsent();
      window.location.reload();
    }
  }

  onModalShow({ modalName }) {
    this._extendUxLogicAfterShowing(modalName);
    this._extendBusinessLogicAfterShowing(modalName);
  }

  onModalHide({ modalName }) {
    this._extendUxLogicAfterHiding(modalName);
  }

  setGeo(geo) {
    this.geo.country = geo?.country || DEFAULT_COUNTRY;
  }

  _saveConsentToMemory(consent) {
    this._consent = consent;
  }

  _saveConsent() {
    this._lib.setCookieData({ value: this._consent.cookie });

    const event = new CustomEvent('cc:onSaveConsent');

    window.dispatchEvent(event);
  }

  _clearConsent() {
    cookie.remove('cc_cookie', this._cookieOptions);
  }

  async _applyConsent() {
    const { accepted, rejected } = this._getFilteredCategories();

    this._loadAllScripts({ accepted, rejected });
    this._clearCookiesForRejectedCategories(rejected, { clearOutdated: true });
  }

  _checkChangedConsent(changedCategories) {
    const { rejected } = this._getFilteredCategories();
    const hasNewRejectedCategories = rejected.map((cat) => cat.name).some((item) => changedCategories.includes(item));

    if (!hasNewRejectedCategories) return;

    window.location.reload();
  }

  _getFilteredCategories() {
    const accepted = [];
    const rejected = [];

    for (const category of this._categories) {
      if (category.readOnly) continue;

      if (this._consent.categories.includes(category.name)) {
        accepted.push(category);
      } else {
        rejected.push(category);
      }
    }

    return { accepted, rejected };
  }

  _loadAllScripts({ accepted, rejected }) {
    const scripts = Array.from(document.querySelectorAll('[type="text/plain"]'));
    const acceptedCategories = accepted.map((cat) => cat.name);
    const rejectedCategories = rejected.map((cat) => cat.name);

    for (const script of scripts) {
      const isConsentScript = script.dataset.consent || '';
      const scriptCategories = script.dataset.categories || '';

      if (!isConsentScript || !scriptCategories) continue;

      const isInversed = script.hasAttribute('inversed');
      const scriptCategoriesList = scriptCategories.split(' ');

      const needToLoadScript =
        (!isInversed && scriptCategoriesList.every((item) => acceptedCategories.includes(item))) ||
        (isInversed && scriptCategoriesList.some((item) => rejectedCategories.includes(item)));

      if (needToLoadScript) this._loadScript(script);
    }
  }

  async _clearCookiesForRejectedCategories(categories, options = {}) {
    const rejectedCategories = categories.map((cat) => cat.name);

    for (const category of this._categories) {
      if (category.readOnly) continue;

      for (const cookieItem of category.autoClear.cookies) {
        const hasRejectedCategories =
          rejectedCategories.includes(category.name) ||
          (Array.isArray(cookieItem.relatedCategories) &&
            cookieItem.relatedCategories.some((item) => rejectedCategories.includes(item)));

        if (!hasRejectedCategories) continue;

        this._removeCookie(cookieItem);
      }
    }

    if (options.clearOutdated) {
      for (const cookieItem of this._config.outdatedCookies) {
        this._removeCookie(cookieItem);
      }
    }
  }

  async _sendConsentToLogger() {
    const data = {
      ...pick(this._consent, [
        'consentId',
        'categories',
        'revision',
      ]),
      consentTimestamp: dayjs(this._consent.consentTimestamp).toDate(),
      lastConsentTimestamp: dayjs(this._consent.lastConsentTimestamp).toDate(),
      expirationTime: dayjs(this._consent.expirationTime).toDate(),
      userId: cookie.get('user_id') || null,
      userAgent: isBrowser ? window.navigator.userAgent : '',
    };

    const cookieLoggerDomain = this._getCookieLoggerDomain();
    const cookieLoggerUrl = `https://${cookieLoggerDomain}/api/cookie-consent/save`;

    const response = await fetch(cookieLoggerUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json;charset=utf-8' },
      body: JSON.stringify(data),
    })
      .catch((e) => console.log(e));

    if (!response || !response.ok || response.status !== 200) {
      throw new Error('Data not sent');
    }
  }

  _getCookieLoggerDomain() {
    const PROD_MAIN_DOMAIN = 'any.run';
    const PROD_MULTILINGUAL_SECOND_LEVEL_DOMAIN = 'anyrun';

    const PROD_SUB_DOMAIN = 'cookie';
    const TEST_SUB_DOMAIN = 'cookie-logger-test';

    const currentDomain = getCurrentDomain();
    const isProdStage =
      currentDomain === PROD_MAIN_DOMAIN ||
      currentDomain.startsWith(PROD_MULTILINGUAL_SECOND_LEVEL_DOMAIN);

    const domain = isProdStage ? PROD_MAIN_DOMAIN : currentDomain;
    const subDomain = isProdStage ? PROD_SUB_DOMAIN : TEST_SUB_DOMAIN;

    return `${subDomain}.${domain}`;
  }

  _loadScript(script) {
    const code = script.innerHTML;
    const newScript = document.createElement('script');

    for (let i = 0; i < script.attributes.length; i++) {
      const attribute = script.attributes[i];

      if (attribute.name === 'type') continue;

      newScript.setAttribute(attribute.name, attribute.value);
    }

    newScript.setAttribute('type', 'text/javascript');
    newScript.innerHTML = code;

    script.replaceWith(newScript);
  }

  _removeCookie(cookieData) {
    const options = {};

    if (cookieData.domain) options.domain = cookieData.domain;
    if (cookieData.path) options.path = cookieData.path;

    if (typeof cookieData.name === 'string') {
      cookie.remove(cookieData.name, options);
    } else {
      cookie.removeByRegex(cookieData.name, options);
    }
  }

  _getCategories() {
    return Object.keys(this._config.categories).map((name) => ({
      name,
      ...this._config.categories[name]
    }));
  }

  _sendUpdateEventToGA() {
    const { dataLayer } = window;

    if (!dataLayer) return;

    dataLayer.push({ event: 'client-consent-update' });
  }

  _extendBusinessLogicAfterShowing(modalName) {
    if (modalName !== 'consentModal') return;

    const DEFAULT_LANGUAGE = 'en';
    const LANGUAGE_BY_COUNTRY = {
      IT: 'it',
      UK: 'en',
      US: 'us'
    };
    const CLOSE_BUTTON_CONFIG_BY_COUNTRY = {
      IT: {
        view: 'icon',
        handler: () => {
          this._lib.acceptCategory([]);
          this._lib.hide();
        },
        attributes: [{ name: 'data-sm-id', value: 'cc-banner-close-it' }]
      },
      US: {
        view: 'icon',
        handler: () => {
          this._lib.acceptCategory('all');
          this._lib.hide();
        },
        attributes: [{ name: 'data-sm-id', value: 'cc-banner-close-us' }]
      }
    };
    const COUNTRIES_WITH_EXTENDED_LOGIC = Object.keys(CLOSE_BUTTON_CONFIG_BY_COUNTRY);

    const country = this.geo.country || DEFAULT_COUNTRY;
    const language = LANGUAGE_BY_COUNTRY[country] || DEFAULT_LANGUAGE;

    this._lib.setLanguage(language, true);
    this._language = language;

    if (!COUNTRIES_WITH_EXTENDED_LOGIC.includes(country)) return;

    const btnConfig = CLOSE_BUTTON_CONFIG_BY_COUNTRY[country];

    addCloseButton(btnConfig);
    addLanguageClassForContainer(language);
  }

  _extendUxLogicAfterShowing(modalName) {
    if (modalName !== 'preferencesModal') return;

    bodyScroll.lock();
    setPreferencesCategoryValuesByLanguage(this._language);
  }

  _extendUxLogicAfterHiding(modalName) {
    if (modalName !== 'preferencesModal') return;

    bodyScroll.unlock();
    setPreferencesCategoryValuesByLanguage(this._language);
  }
}
